"use client";

import React, { useId } from "react";
import {
  LINK_POSITION_BODY,
  LINK_POSITION_CHANNEL_LINEUP_PANEL,
  fireLinkClick,
  fireProductClick
} from "../../services/analytics";
import {
  SORT_RADIO_PRODUCT,
  pageHref,
  sortDisplayLinkName,
  sortRadioLinkName
} from "./lineup-analytics.shared";
import { type SortOption, prepareSortOptions } from "./sort-radios.shared";
import styles from "./sort-radios.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * SortRadios — GH4. A native `<input type="radio">` group, the same hidden-input/visible-label pair
 * `view-toggle.client.tsx` and `tab-tray` already use in this tree.
 */

export interface SortRadiosProps {
  /** GH4 — the content team's options, in its own order. Anything malformed
   *  or duplicated is dropped; nothing usable left draws no control. */
  options?: SortOption[];
  /** The organism root's current sort. Always one of the rendered options'
   *  values, resolved by `resolveSelectedSort` before this mounts. */
  value: string;
  /** Reports the newly chosen option's value. The root is what reacts to it
   *  — this leaf never changes what is on screen itself. */
  onChange: (value: string) => void;
  /** The name the radios share, which makes them one group. */
  paramName?: string;
  /** Accessible name for the group, authored. Not the visible sort wording,
   *  which the header draws beside the group rather than inside it. */
  ariaLabel?: string;
  className?: string;
}

const DEFAULT_PARAM_NAME = "sort";

function SortRadiosImpl({
  options,
  value,
  onChange,
  paramName = DEFAULT_PARAM_NAME,
  ariaLabel = "",
  className
}: SortRadiosProps) {
  const { renderable, malformed, duplicates } = prepareSortOptions(options);
  const groupName = useId();

  if (process.env.NODE_ENV !== "production") {
    if (ariaLabel.trim().length === 0) {
      console.error(
        "[SortRadios] `ariaLabel` is blank, so the group has no accessible " +
          "name and a screen reader announces the options without ever " +
          "saying what is being chosen."
      );
    }
    if (malformed > 0) {
      console.warn(
        `[SortRadios] dropped ${malformed} malformed option${malformed === 1 ? "" : "s"}. ` +
          "Every option needs a non-blank `value` and `label`."
      );
    }
    for (const duplicate of duplicates) {
      console.warn(`[SortRadios] dropped a duplicate option value "${duplicate}".`);
    }
    if (renderable.length > 0 && !renderable.some(option => option.value === value)) {
      console.error(
        `[SortRadios] controlled value "${value}" matches no option, so no ` +
          "option is selected and the group looks broken. Keep `value` in " +
          "step with `options`."
      );
    }
  }

  /**
   * AN4 — one sort change, three reports, which is what the control arm sends and therefore what
   * AC-10d's side-by-side diff expects: 1. a `link_click` named `Display_{option}`, cased as
   * authored; 2. a `link_click` named `sortby_{option}`, lower-cased; 3. the radio group's own
   * `product_click`, which describes the control and never the chosen option.
   */
  const report = (chosenValue: string) => {
    const chosen = renderable.find(option => option.value === chosenValue);
    if (!chosen || !chosen.label) {
      return;
    }

    /* Only the second one carries a destination. */
    fireLinkClick({
      linkName: sortDisplayLinkName(chosen.label),
      linkPosition: LINK_POSITION_BODY
    });
    fireLinkClick({
      linkDestination: pageHref(),
      linkName: sortRadioLinkName(chosen.label),
      linkPosition: LINK_POSITION_CHANNEL_LINEUP_PANEL
    });
    fireProductClick(SORT_RADIO_PRODUCT);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    /* A native radio group never fires `change` for the option already
       checked, so re-choosing the current sort needs no guard here. */
    report(event.target.value);
    onChange(event.target.value);
  };

  if (renderable.length === 0) {
    return null;
  }

  return (
    <form className={[styles.form, className].filter(Boolean).join(" ")}>
      <fieldset className={styles.fieldset}>
        {ariaLabel.trim() !== "" && <legend className="dds-sr-only">{ariaLabel.trim()}</legend>}
        {renderable.map((option, index) => {
          const id = `${groupName}-opt${index}`;
          return (
            <span className={styles.slot} key={option.value}>
              <input
                checked={option.value === value}
                className={styles.input}
                id={id}
                name={paramName}
                onChange={handleChange}
                type="radio"
                value={option.value}
              />
              <label className={styles.label} htmlFor={id}>
                {/* GH9 — "white rings, with a white dot when selected." Drawn rather than native, because `accent-color` cannot paint a ring and a dot separately. Aria-hidden: the input beside it already carries the checked state. */}
                <span aria-hidden="true" className={styles.ring} />
                <span className={styles.text}>{option.label}</span>
              </label>
            </span>
          );
        })}
      </fieldset>
    </form>
  );
}
export const SortRadios = withSafeRender(SortRadiosImpl, "SortRadios");

export type { SortOption };

export default SortRadios;
